import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Link,
  Drawer,
  IconButton,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CloseIcon from '@mui/icons-material/Close';
import dateFormat from '../../utils/dateFormat';
import { useAccount } from '../../contexts/Account';
import { getUpcomingBoardMeetings } from '../../services/advisor_services';
import { useHistory } from 'react-router-dom';
import CalendarSyncMenu from '../../components/CalendarSyncMenu';
import ACSnackbar from '../../components/ACSnackbar';
import { SnackbarType } from '../../services/utils/types';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import { convertTimezoneToAbbr } from '../../utils/dateFormat';
import { getAdvisorQueryParams } from '../../utils/queryParams';
import eventBus, { eventBusValues } from '../../eventBus';

const UpcomingBoardMeetings = () => {
  const { accountDetails, isFreemium, isFreeTrial, currentPlan } = useAccount();
  const [open, setOpen] = useState(false);

  const [meetings, setMeetings] = useState([]);
  const history = useHistory();
  const [calendarAnchorEl, setCalendarAnchorEl] = useState(null);

  const [googleSync, setGoogleSync] = useState(
    accountDetails?.calendarTokens?.google || false
  );
  const [appleSync, setAppleSync] = useState(
    accountDetails?.calendarTokens?.apple || false
  );

  const [calendarSynced, setCalendarSynced] = useState(false);

  const [isLoading, setIsLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState('');
  const [snackSeverity, setSnackSeverity] = useState(SnackbarType.SUCCESS);

  const advisorId = accountDetails?.id;
  const companyIds = accountDetails?.boards?.memberBoards?.map(
    (board) => board.id
  );

  const showSnackbar = (message, severity = SnackbarType.SUCCESS) => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleOpenCalendarMenu = (event) => {
    setCalendarAnchorEl(event.currentTarget);
  };

  const handleCloseCalendarMenu = () => {
    setCalendarAnchorEl(null);
  };

  useEffect(() => {
    const fetchMeetings = async () => {
      const response = await getUpcomingBoardMeetings({
        advisorId: accountDetails.id,
      });
      const keyDates = response.data.getUpcomingBoardMeetings;
      const createdAt = new Date(accountDetails.createdAt);
      const now = new Date();
      const bioRewriteDate = new Date(createdAt);
      bioRewriteDate.setMonth(bioRewriteDate.getMonth() + 6);

      if (bioRewriteDate > now) {
        keyDates.push({
          meetingTime: bioRewriteDate,
          header: 'Advisor Biography Refresh',
          postTitle: 'Complimentary Advisor Bio Rewrite for You',
          subtext: 'Two options emailed to you',
        });
      }

      const advisorRoadmapDate = new Date(createdAt);
      advisorRoadmapDate.setMonth(advisorRoadmapDate.getMonth() + 10);

      if (advisorRoadmapDate > now) {
        keyDates.push({
          meetingTime: advisorRoadmapDate,
          header: 'Long-Term Professional Goals Update',
          postTitle: 'Custom Long-Term Professional Roadmap',
          subtext: 'Created and emailed to you',
        });
      }
      setMeetings(
        keyDates.sort((a, b) => {
          return new Date(a.meetingTime) - new Date(b.meetingTime);
        })
      );
    };

    function handleEventDrawer(data) {
      if (data.action === 'open') {
        setOpen(true);
      } else if (data.action === 'close') {
        setOpen(false);
      }
    }

    eventBus.on(eventBusValues.keyDatesAction, handleEventDrawer);

    fetchMeetings();

    return () => {
      eventBus.remove(eventBusValues.keyDatesAction, handleEventDrawer);
    };
  }, [accountDetails.id]);

  useEffect(
    function isCalendarSynced() {
      setCalendarSynced(googleSync || appleSync);
    },
    [googleSync, appleSync, isLoading]
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => setOpen(false)}
      PaperProps={{
        sx: {
          width: { xxs: '100%', xs: 600 },
          p: 2,
        },
      }}
    >
      <IconButton
        onClick={() => setOpen(false)}
        sx={{
          position: 'absolute',
          top: 8,
          right: 8,
          zIndex: 10,
          display: { xxs: 'inline-flex', sm: 'none' },
        }}
        aria-label="close"
      >
        <CloseIcon />
      </IconButton>
      <Paper
        id="upcoming-board-meetings-tile"
        elevation={3}
        sx={{
          padding: '15px',
          borderRadius: '12px',
          backgroundColor: '#fff',
          marginBottom: '20px',
          boxShadow: 'none',
          minHeight: '100%',
          mt: 4,
        }}
      >
        <Typography
          sx={{
            fontWeight: '600',
            fontSize: 20,
            lineHeight: '24px',
            color: '#001030',
            marginBottom: '15px',
          }}
        >
          Key Upcoming Dates
        </Typography>

        {calendarSynced ? null : (
          <Typography
            sx={{
              fontSize: '12px',
              color: '#4D586F',
              fontWeight: 400,
              lineHeight: '18px',
            }}
          >
            Below you will find upcoming board meetings for any boards you have
            joined as well as other key dates that will automatically update for
            you.
          </Typography>
        )}

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
            marginTop: '15px',
          }}
        >
          {meetings.map((meeting, index) => {
            const dateObj = new Date(meeting.meetingTime);
            const month = dateFormat(dateObj, 'mmm');
            const dayNumber = dateFormat(dateObj, 'd');
            const timeFormatted = dateFormat(dateObj, 'h:MM TT');
            const localZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            const timezoneAbbreviation = convertTimezoneToAbbr(localZone);

            return (
              <Paper
                className={
                  meeting.subtext
                    ? 'key-board-meeting'
                    : 'upcoming-board-meeting'
                }
                key={index}
                elevation={1}
                onClick={() => {
                  if (meeting.id) {
                    setOpen(false);
                    history.push(`/post/${meeting.id}`);
                  }
                }}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid #C9CDD3',
                  backgroundColor: '#fff',
                  boxShadow: 'none',
                  height: '108px',
                  cursor: !meeting.subtext ? 'pointer' : 'default',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: '84px',
                    height: '84px',
                  }}
                >
                  <Box
                    sx={{
                      backgroundColor: '#7BC8FF',
                      color: '#002F51',
                      borderTopLeftRadius: 8,
                      borderTopRightRadius: 8,
                      textAlign: 'center',
                      p: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: '500',
                        fontSize: '14px',
                        lineHeight: '21px',
                      }}
                    >
                      {month}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      backgroundColor: '#EEF8FF',
                      color: '#001030',
                      borderBottomLeftRadius: 8,
                      borderBottomRightRadius: 8,
                      textAlign: 'center',
                      p: '4px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: '500',
                        fontSize: '24px',
                        lineHeight: '36px',
                        margin: '6px 0',
                      }}
                    >
                      {dayNumber}
                    </Typography>
                  </Box>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    maxWidth: 'calc(100% - 100px)',
                    marginLeft: '16px',
                  }}
                >
                  <Typography
                    sx={{ fontSize: '12px', color: '#4D586F', fontWeight: 400 }}
                  >
                    {meeting.postCompanyContact
                      ? meeting.postCompanyContact.companyName
                      : meeting.header}
                  </Typography>

                  <Box
                    sx={{
                      height: '48px',
                      display: 'flex',
                      alignItems: 'center',
                      minWidth: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '14px',
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {meeting.postTitle}
                    </Typography>
                  </Box>
                  <Typography
                    noWrap
                    sx={{
                      fontSize: '12px',
                      color: '#4D586F',
                      fontWeight: 400,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      minWidth: 0,
                      width: '100%',
                    }}
                  >
                    <span style={{ color: '#2b71f6' }}>•</span>{' '}
                    {meeting.subtext
                      ? meeting.subtext
                      : timeFormatted + ' ' + timezoneAbbreviation}
                  </Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>

        <Button
          id="Sync Calendar"
          variant="contained"
          startIcon={<SyncIcon />}
          onClick={handleOpenCalendarMenu}
          disabled={isLoading}
          sx={{
            marginTop: '20px',
            textTransform: 'none',
            fontSize: '16px',
            padding: '10px 20px',
            borderRadius: '8px',
            backgroundColor: '#2B71F6',
            color: '#fff',
            fontWeight: '600',
            fontSize: '14px',
            width: '100%',
            '&:hover': {
              backgroundColor: '#2B71F6',
            },
          }}
        >
          {isLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <LoadingLogoSkeleton />
            </div>
          ) : calendarSynced ? (
            'Manage Calendar Sync'
          ) : (
            'Sync Calendar'
          )}
        </Button>

        {currentPlan?.salesforceValues?.billingCycle === 'Monthly' && (
          <Link
            href={`https://register.advisorycloud.com/platform/upgrade-professional-annual/?${getAdvisorQueryParams(
              {
                accountDetails,
                isFreeTrial,
                isFreemium,
              }
            )}`}
            rel="noopener noreferrer"
            sx={{
              textDecoration: 'none',
              fontSize: '12px',
              marginTop: '10px',
              display: 'block',
              textAlign: 'center',
              fontStyle: 'italic',
              textDecoration: 'underline',
            }}
          >
            Upgrade to Annual & Get 4 Months Free
          </Link>
        )}

        <CalendarSyncMenu
          anchorEl={calendarAnchorEl}
          setAnchorEl={setCalendarAnchorEl}
          syncStates={{
            googleSync,
            appleSync,
          }}
          setSyncStates={{
            setGoogleSync,
            setAppleSync,
          }}
          advisorId={advisorId}
          companyIds={companyIds}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          showSnackbar={showSnackbar}
        />
        <ACSnackbar
          style={{ marginTop: isFreemium ? '50px' : '0px' }}
          open={snackbarOpen}
          text={snackMessage}
          severity={snackSeverity}
          onClose={() => {
            setSnackbarOpen(false);
          }}
          autoHideDuration={5000}
        />
      </Paper>
    </Drawer>
  );
};

export default UpcomingBoardMeetings;
